import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  ClickAwayListener,
  IconButton,
  MenuList,
  MenuItem,
  Popper,
  Modal,
  Paper,
  Typography,
  styled,
  Divider,
  Stack,
  SvgIcon,
  Switch,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useModal } from '/packages/shared-components/utils/modal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';

import WorkstreamsMenu from './WorkstreamsMenu/WorkstreamsMenu';
import RequestAccessModal from './RequestAccessModal';
import { useThemeMode } from '../utils/ThemeContext';
import { DarkModeToggleGate } from '/packages/shared-components/utils/launchDarkly/DarkModeToggleGate';
import FavIcon from './FavIcon';
import { commonTokens } from '/packages/shared-components/assets/themes/tokens';

const StyledMenuItem = styled(MenuItem)({
  margin: '0',
  padding: '12px 16px',
  '& + .MuiDivider-root': {
    marginTop: '0',
    marginBottom: '0',
  },
});

const DarkModeToggleSection = () => {
  const { mode, toggleTheme } = useThemeMode();

  return (
    <>
      <Divider />
      <Box component="li" sx={{ pt: 2, pr: 1, pb: 1, pl: 2 }}>
        <Typography variant="overline" color="text.secondary" lineHeight={1}>
          Appearance
        </Typography>
      </Box>
      <StyledMenuItem onClick={toggleTheme}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" gap={1} alignItems="center">
            <DarkModeOutlinedIcon fontSize="small" />
            <span>Dark Mode</span>
          </Stack>
          <Switch edge="end" checked={mode === 'dark'} inputProps={{ 'aria-label': 'Toggle dark mode' }} />
        </Box>
      </StyledMenuItem>
    </>
  );
};

export interface AuthHeaderButtonProps {
  userType: string | null | undefined;
  isPortalAdmin: boolean | null | undefined;
  accepted?: boolean;
  switchRole: () => void;
  open: boolean;
  handleUserClose: () => void;
  handleUserToggle: () => void;
  handleEventChange: () => void;
  logout: () => void;
  changeNavigation: (path: string) => void;
  isAccessManagementAdmin: boolean | null | undefined;
}
const AuthHeaderButton = ({
  userType,
  isPortalAdmin,
  accepted,
  switchRole,
  open,
  handleUserClose,
  handleUserToggle,
  handleEventChange,
  logout,
  changeNavigation,
  isAccessManagementAdmin,
}: AuthHeaderButtonProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const workstreamsAnchorRef = useRef<HTMLButtonElement>(null);

  const [workstreamsOpen, setWorkstreamsOpen] = useState<boolean>(false);
  const { setOpen, setContent } = useModal();

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      handleEventChange();
    }
  };

  const handleClick = () => {
    location.pathname.startsWith('/app') && navigate({ pathname: '/app/terms-of-use', search: location.search });
    !location.pathname.startsWith('/app') &&
      window.location.assign(`${process.env.REACT_APP_BASE_URI}/app/terms-of-use${location.search}`);
  };

  const handleNavigateClick = (path: string) => {
    changeNavigation(path);
    handleUserClose();
    handleWorkstreamClose();
  };

  const handleWorkstreamsOpen = () => setWorkstreamsOpen(true);

  const handleWorkstreamClose = () => setWorkstreamsOpen(false);

  const handleParentMenuToggle = () => {
    handleWorkstreamClose();
    handleUserToggle();
  };
  const handleOpenRequestAccessModal = () => {
    setContent(<RequestAccessModal />);
    setOpen(true);
  };
  const theme = useTheme();

  const disneyLabelColor =
    theme.palette.mode === 'dark' ? commonTokens.componentDark.info.content : theme.palette.info.main; // Blue05 in dark mode

  const isNarrow = useMediaQuery('(max-width:599.5px)');

  const blocksAppNavigation =
    location.pathname.includes('/app/full-story-disclosure') ||
    location.pathname.includes('/app/complete-partner-profile');

  return (
    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', position: 'relative', float: 'right' }}>
      <IconButton
        onClick={handleParentMenuToggle}
        color="inherit"
        ref={anchorRef}
        aria-label={`${open ? 'Close' : 'Open'} user menu`}
        aria-haspopup="true"
        aria-controls={open ? 'menu-list-profile' : undefined}
        size="large"
      >
        <PersonIcon
          sx={{
            color: '#1D2026',
            background: '#F0F2F4',
            borderRadius: '50%',
            padding: '6px',
            marginRight: '6px',
          }}
          fontSize="large"
        />
        {open ? (
          <ExpandLessIcon sx={{ color: 'white' }} fontSize="small" />
        ) : (
          <ExpandMoreIcon sx={{ color: 'white' }} fontSize="small" />
        )}
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        disablePortal
        placement="bottom-end"
        sx={{ zIndex: 15, width: '250px' }}
      >
        <ClickAwayListener onClickAway={handleUserClose}>
          <Paper sx={{ padding: '8px 0' }}>
            {isPortalAdmin && (
              <Stack direction="row" gap={1} alignItems="center" sx={{ py: 1, pr: 1, pl: 2 }}>
                <Typography variant="overline" color="text.secondary" lineHeight={1}>
                  User Type:
                </Typography>
                <Stack
                  direction="row"
                  gap={0.5}
                  alignItems="center"
                  sx={{ color: userType === 'Disney' ? disneyLabelColor : undefined }}
                >
                  {userType === 'Disney' ? (
                    <SvgIcon viewBox="0 0 64 64" sx={{ width: '18px', height: '18px' }}>
                      <FavIcon color={disneyLabelColor} />
                    </SvgIcon>
                  ) : (
                    <HandshakeOutlinedIcon style={{ width: '18px', height: '18px' }} />
                  )}
                  <Typography variant="body2">{userType}</Typography>
                </Stack>
              </Stack>
            )}
            <MenuList id="menu-list-profile" onKeyDown={handleListKeyDown} style={{ padding: '0' }}>
              {isPortalAdmin && (
                <StyledMenuItem onClick={switchRole} disabled={!accepted || blocksAppNavigation}>
                  Switch User Type to {userType === 'Partner' ? 'Disney' : 'Partner'}
                </StyledMenuItem>
              )}
              {isPortalAdmin && <Divider />}
              <Box component="li" sx={{ pt: 2, pr: 1, pb: 1, pl: 2 }}>
                <Typography variant="overline" color="text.secondary" lineHeight={1}>
                  Administration
                </Typography>
              </Box>
              <StyledMenuItem onClick={() => handleNavigateClick('/app/admin/settings')} disabled={blocksAppNavigation}>
                Account Settings
              </StyledMenuItem>
              {userType === 'Disney' && isAccessManagementAdmin && (
                <StyledMenuItem
                  onClick={() => handleNavigateClick('/app/admin/access-management')}
                  disabled={blocksAppNavigation}
                >
                  Access Management
                </StyledMenuItem>
              )}
              {userType === 'Disney' && isAccessManagementAdmin && (
                <StyledMenuItem
                  onClick={() => handleNavigateClick('/app/admin/notifications')}
                  disabled={blocksAppNavigation}
                >
                  Notifications
                </StyledMenuItem>
              )}
              {userType === 'Disney' && !isAccessManagementAdmin && (
                <StyledMenuItem onClick={handleOpenRequestAccessModal} disabled={blocksAppNavigation}>
                  Request Access
                </StyledMenuItem>
              )}

              {userType === 'Disney' && (
                <StyledMenuItem
                  onMouseOver={!isNarrow ? handleWorkstreamsOpen : undefined}
                  onMouseLeave={!isNarrow ? handleWorkstreamClose : undefined}
                  onClick={isNarrow ? handleWorkstreamsOpen : undefined}
                  sx={{ padding: '0' }}
                  disabled={blocksAppNavigation}
                >
                  <Box
                    ref={workstreamsAnchorRef}
                    sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}
                  >
                    <div>Workstreams</div>
                    <KeyboardArrowRightIcon sx={{ alignSelf: 'flex-end' }} />
                  </Box>
                  {!isNarrow && (
                    <Popper
                      open={workstreamsOpen}
                      anchorEl={workstreamsAnchorRef.current}
                      disablePortal={true}
                      placement="left-start"
                      sx={{ zIndex: 15, width: '250px' }}
                    >
                      <WorkstreamsMenu changeNavigation={changeNavigation} handleClose={handleWorkstreamClose} />
                    </Popper>
                  )}
                </StyledMenuItem>
              )}
              <DarkModeToggleGate>
                <DarkModeToggleSection />
              </DarkModeToggleGate>
              <Divider />
              <StyledMenuItem onClick={handleClick} style={{ borderBottom: '0' }}>
                Terms of Use
              </StyledMenuItem>
              <StyledMenuItem onClick={logout}>Logout</StyledMenuItem>
              {isNarrow && workstreamsOpen && (
                <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}>
                  <WorkstreamsMenu changeNavigation={changeNavigation} handleClose={handleWorkstreamClose} responsive />
                </Box>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export { AuthHeaderButton };
